import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TribeLogo, { TribeMark } from "@/components/TribeLogo";
import { Eye, EyeOff, Loader2, CheckCircle2, Building2 } from "lucide-react";
import { API } from "@/lib/api";


function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
      });
      setSuccess(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface px-6">
        <div className="w-full max-w-sm text-center">
          <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={28} className="text-ink" />
          </div>
          <h2 className="text-2xl font-bold text-ink tracking-tight mb-2">Registration Submitted</h2>
          <p className="text-sm text-ink/50 mb-6">
            Your account is pending admin approval. You'll be able to sign in once approved.
          </p>
          <Button onClick={() => navigate("/login")} data-testid="back-to-login-button" className="rounded-full bg-purple-500 hover:bg-purple-600 text-white">
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex font-sans bg-surface">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <TribeMark size={40} />
            <div>
              <TribeLogo size="sm" />
              <p className="text-xs text-ink/40 mt-0.5">Vendor Registration</p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-ink tracking-tight">Create Account</h2>
            <p className="text-sm text-ink/50 mt-1.5">Register as a vendor to manage your loyalty program</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-semibold text-ink/60 uppercase tracking-widest">Full Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="John Smith" data-testid="register-name-input" className="h-11 rounded-xl border-ink/15 bg-white" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold text-ink/60 uppercase tracking-widest">Email Address</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="vendor@business.com" data-testid="register-email-input" className="h-11 rounded-xl border-ink/15 bg-white" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold text-ink/60 uppercase tracking-widest">Password</Label>
              <div className="relative">
                <Input id="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={12} placeholder="Min 12 chars, mixed case, digit, symbol" data-testid="register-password-input" className="h-11 pr-10 rounded-xl border-ink/15 bg-white" />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/30 hover:text-ink/60">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && <div className="px-4 py-2.5 rounded-xl bg-purple-50 border border-danger/30 text-danger text-sm" data-testid="register-error">{error}</div>}

            <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full h-11 rounded-full bg-purple-500 hover:bg-purple-600 text-white font-semibold">
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {loading ? "Registering..." : "Register"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-ink/50 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-ink font-semibold hover:text-purple-700" data-testid="go-to-login-link">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-purple-500 items-center justify-center px-12">
        <div className="max-w-md text-center">
          <TribeLogo variant="purple" size="lg" className="justify-center mb-8 text-4xl" />
          <h3 className="text-3xl font-display font-bold text-white mb-4 tracking-tight">Join Our Loyalty Network</h3>
          <p className="text-white/80 text-base leading-relaxed font-body">Create and manage your own rewards program for your customers.</p>
        </div>
      </div>
    </div>
  );
}
